import type { Router } from "vue-router";
import {
    syncActiveOrganisationToLegacyStore,
    usePiniaStore,
} from "@/stores/store";
import { useOrganisationStore } from "@/stores/organisationStore";
import { showAlert } from "@/methods/errorHandling";
import { resetRequestContext } from "@/methods/requestContext";

/**
 * The minimal slice of the router needed to leave org-scoped pages before
 * switching. Passed in by the caller (rather than imported) to avoid a
 * circular dependency with `@/router/index`.
 */
export type OrgSwitchNavigator = Pick<Router, "currentRoute" | "push">;

/**
 * Makes an organisation active, cancels in-flight requests for the previous
 * organisation and confirms the switch to the user. Does not navigate: use
 * this from navigation guards, where the navigation in progress already
 * leaves the current page.
 * @param shortcode The shortcode of the organisation to activate.
 * @returns True if the active organisation changed.
 */
export const activateOrganisation = (shortcode: string): boolean => {
    const organisationStore = useOrganisationStore();
    if (shortcode === organisationStore.getActiveOrganisationShortcode) {
        return false;
    }

    if (!organisationStore.selectOrganisation(shortcode)) return false;

    // Cancel requests from the previous organisation. This runs in the same
    // tick as the state change, so it happens before any watchers refetch.
    resetRequestContext();

    syncActiveOrganisationToLegacyStore();
    organisationStore.setPreferredOrganisation(
        usePiniaStore().getUserEmail,
        shortcode,
    );
    showAlert("alerts.organisationSwitched", "success", {
        organisation:
            organisationStore.getActiveOrganisation?.name ?? shortcode,
    });
    return true;
};

/**
 * Switches organisation in place (e.g. from the organisation switcher).
 * If the current route shows a resource belonging to the previous
 * organisation, navigates to its fallback route first so that resource is
 * never requested under the new organisation.
 * Do not call this from a navigation guard: the fallback navigation would
 * cancel the navigation being guarded. Use activateOrganisation instead.
 * @param shortcode The shortcode of the organisation to switch to.
 * @param navigator The router instance used to navigate to the fallback.
 * @returns True if the active organisation changed.
 */
export const switchActiveOrganisation = async (
    shortcode: string,
    navigator: OrgSwitchNavigator,
): Promise<boolean> => {
    const organisationStore = useOrganisationStore();
    if (shortcode === organisationStore.getActiveOrganisationShortcode) {
        return false;
    }

    const requestedIsAvailable =
        organisationStore.getAvailableOrganisations.some(
            (organisation) => organisation.shortcode === shortcode,
        );
    if (!requestedIsAvailable) return false;

    // Leave org-scoped pages before switching, so their components unmount
    // and never refetch their resource under the new organisation.
    const fallback = navigator.currentRoute.value.meta.orgSwitchFallback;
    if (fallback) {
        try {
            const navigationFailure = await navigator.push(fallback);
            // Navigation was blocked (e.g. an unsaved-changes guard): don't switch
            if (navigationFailure) return false;
        } catch {
            // Navigation failed unexpectedly: don't switch organisations
            return false;
        }
    }

    return activateOrganisation(shortcode);
};
